import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin, Navigation, Phone, User, Car, Clock, Route,
  Play, Square, RefreshCw, Crosshair, Flag, Plus, ArrowLeft,
  CheckCircle, AlertCircle, Gauge, Camera
} from 'lucide-react';
import Card, { CardContent, CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { journeyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LiveTrackingMap from '../../components/maps/LiveTrackingMap';
import {
  getCurrentPosition,
  watchPosition,
  formatCoordinates,
  formatDistance,
  getAccuracyIndicator,
  calculateDistance
} from '../../services/locationService';
import { formatDateTime, formatCurrency, formatVehicleName } from '../../lib/utils';
import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ActiveTrip = () => {
  const { bookingId, journeyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [journey, setJourney] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Location state
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [gpsEnabled, setGpsEnabled] = useState(() => {
    return localStorage.getItem('gpsTrackingEnabled') === 'true';
  });
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const gpsEnabledRef = useRef(localStorage.getItem('gpsTrackingEnabled') === 'true');

  // Trip state
  const [tripStarted, setTripStarted] = useState(false);
  const tripStartedRef = useRef(false);
  const [kmReading, setKmReading] = useState('');
  const [updating, setUpdating] = useState(false);

  // Checkpoint modal
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);
  const [checkpointNote, setCheckpointNote] = useState('');
  const [checkpointPurpose, setCheckpointPurpose] = useState('Stop');

  // Odometer photo
  const [odometerPhoto, setOdometerPhoto] = useState(null); // preview URL
  const [odometerPhotoUploading, setOdometerPhotoUploading] = useState(false);
  const [odometerPhotoUploaded, setOdometerPhotoUploaded] = useState(false);
  const odometerFileRef = useRef(null);

  // End trip confirmation modal
  const [showEndTripModal, setShowEndTripModal] = useState(false);
  const [tollCharges, setTollCharges] = useState('');
  const [parkingCharges, setParkingCharges] = useState('');
  const [otherCharges, setOtherCharges] = useState('');
  const [otherChargesNote, setOtherChargesNote] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountNote, setDiscountNote] = useState('');

  // Refs
  const watchCleanupRef = useRef(null);
  const socketRef = useRef(null);
  const updateIntervalRef = useRef(null);

  // Refs to track latest values for use inside GPS callbacks (must be before fetchData)
  const journeyRef = useRef(null);
  const kmReadingRef = useRef(kmReading);
  useEffect(() => { journeyRef.current = journey; }, [journey]);
  useEffect(() => { kmReadingRef.current = kmReading; }, [kmReading]);

  // Start GPS watch — called directly, not via useEffect (must be before fetchData)
  const startGpsWatch = () => {
    // Stop any existing watch first
    if (watchCleanupRef.current) {
      watchCleanupRef.current();
      watchCleanupRef.current = null;
    }

    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser');
      return;
    }

    setLocationError(null);
    console.log('Starting GPS watch...');

    // Check permission first
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        console.log('GPS permission status:', result.state);
        if (result.state === 'denied') {
          setLocationError('Location permission denied. Please enable in browser settings.');
          setGpsEnabled(false);
          gpsEnabledRef.current = false;
          return;
        }
        // Permission granted or prompt — proceed with watch
        doStartWatch();
      }).catch(() => {
        // permissions API not supported, try anyway
        doStartWatch();
      });
    } else {
      doStartWatch();
    }
  };

  const doStartWatch = () => {
    // Get immediate position
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        console.log('Got immediate GPS:', pos.coords.latitude, pos.coords.longitude);
        setCurrentLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        });
        setGpsAccuracy(pos.coords.accuracy);
      },
      (err) => {
        console.warn('Immediate position failed:', err.message);
        if (err.code === 1) {
          setLocationError('Location permission denied');
          setGpsEnabled(false);
          gpsEnabledRef.current = false;
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );

    // Start continuous watch
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };

        setCurrentLocation(loc);
        setGpsAccuracy(position.coords.accuracy);
        setLocationError(null);

        // Add to local history
        setLocationHistory(prev => [
          ...prev,
          { ...loc, timestamp: new Date() }
        ]);

        // Emit via socket
        const currentJourney = journeyRef.current;
        if (socketRef.current && currentJourney) {
          socketRef.current.emit('driver-location-update', {
            journeyId: currentJourney._id,
            trackingCode: currentJourney.trackingCode,
            latitude: loc.latitude,
            longitude: loc.longitude
          });
        }
      },
      (error) => {
        console.error('GPS watch error:', error.message);
        setLocationError(error.message);
        if (error.code === 1) {
          setGpsEnabled(false);
          gpsEnabledRef.current = false;
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );

    watchCleanupRef.current = () => {
      console.log('Stopping GPS watch');
      navigator.geolocation.clearWatch(watchId);
    };
  };

  const stopGpsWatch = () => {
    if (watchCleanupRef.current) {
      watchCleanupRef.current();
      watchCleanupRef.current = null;
    }
  };

  // Fetch journey/booking data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (journeyId) {
        // Fetch existing journey
        const response = await journeyAPI.getJourneyById(journeyId);
        if (response.data.success) {
          const journeyData = response.data.journey;
          setJourney(journeyData);
          journeyRef.current = journeyData;
          const isInProgress = journeyData.status === 'in-progress';
          setTripStarted(isInProgress);
          tripStartedRef.current = isInProgress;
          setKmReading(journeyData.liveTracking?.lastKmReading || journeyData.startOdometer || '');
          kmReadingRef.current = journeyData.liveTracking?.lastKmReading || journeyData.startOdometer || '';

          if (journeyData.liveTracking?.currentLocation) {
            setCurrentLocation({
              latitude: journeyData.liveTracking.currentLocation.latitude,
              longitude: journeyData.liveTracking.currentLocation.longitude
            });
          }

          if (journeyData.liveTracking?.locationHistory) {
            setLocationHistory(journeyData.liveTracking.locationHistory);
          }

          // If trip is in-progress, auto-enable GPS and start watching
          if (isInProgress) {
            setGpsEnabled(true);
            gpsEnabledRef.current = true;
            localStorage.setItem('gpsTrackingEnabled', 'true');
            startGpsWatch();
          }
        }
      } else if (bookingId) {
        // Fetch booking and check for existing journey
        const response = await journeyAPI.getActiveJourneyForBooking(bookingId);
        if (response.data.success) {
          if (response.data.journey) {
            const journeyData = response.data.journey;
            setJourney(journeyData);
            journeyRef.current = journeyData;
            const isInProgress = journeyData.status === 'in-progress';
            setTripStarted(isInProgress);
            tripStartedRef.current = isInProgress;
            setKmReading(journeyData.liveTracking?.lastKmReading || journeyData.startOdometer || '');
            kmReadingRef.current = journeyData.liveTracking?.lastKmReading || journeyData.startOdometer || '';

            // If trip is in-progress, auto-enable GPS and start watching
            if (isInProgress) {
              setGpsEnabled(true);
              gpsEnabledRef.current = true;
              localStorage.setItem('gpsTrackingEnabled', 'true');
              startGpsWatch();
            }
          } else if (response.data.booking) {
            setBooking(response.data.booking);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load trip data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Always auto-enable GPS when driver opens this page
    setGpsEnabled(true);
    gpsEnabledRef.current = true;
    localStorage.setItem('gpsTrackingEnabled', 'true');
    startGpsWatch();
  }, [journeyId, bookingId]);

  // Setup Socket.IO
  useEffect(() => {
    if (!journey?._id) return;

    socketRef.current = io(API_URL);

    socketRef.current.on('connect', () => {
      console.log('Socket connected for trip tracking');
      socketRef.current.emit('join-journey', journey._id);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave-journey', journey._id);
        socketRef.current.disconnect();
      }
    };
  }, [journey?._id]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      stopGpsWatch();
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  // Auto-update location to server every 30 seconds
  useEffect(() => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }

    if (tripStartedRef.current && gpsEnabledRef.current && currentLocation && journeyRef.current) {
      updateIntervalRef.current = setInterval(() => {
        sendLocationUpdate(currentLocation.latitude, currentLocation.longitude);
      }, 30000);
    }

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [currentLocation]);

  const sendLocationUpdate = async (latitude, longitude) => {
    const currentJourney = journeyRef.current;
    if (!currentJourney?._id) return;

    try {
      await journeyAPI.updateLocation(currentJourney._id, {
        latitude,
        longitude,
        kmReading: kmReadingRef.current ? parseFloat(kmReadingRef.current) : null
      });
    } catch (err) {
      console.error('Error sending location update:', err);
    }
  };

  // Start Trip
  const handleStartTrip = async () => {
    if (!kmReading) {
      setError('Please enter the starting KM reading');
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      // Get current location first
      let location = currentLocation;
      if (!location) {
        try {
          location = await getCurrentPosition();
          setCurrentLocation(location);
        } catch (err) {
          console.warn('Could not get location:', err);
        }
      }

      let response;

      if (journey) {
        // Start existing journey
        response = await journeyAPI.startJourney(journey._id, {
          startOdometer: parseFloat(kmReading),
          latitude: location?.latitude,
          longitude: location?.longitude
        });
      } else if (booking) {
        // Create new journey from booking
        const journeyData = {
          bookingId: booking._id,
          carId: booking.carId._id || booking.carId,
          driverId: user._id,
          customerId: booking.customerId?._id || booking.customerId,
          fromLocation: {
            address: typeof booking.pickupLocation === 'string'
              ? booking.pickupLocation
              : booking.pickupLocation?.address || 'Pickup',
            coordinates: location ? {
              latitude: location.latitude,
              longitude: location.longitude
            } : undefined
          },
          toLocation: {
            address: typeof booking.dropoffLocation === 'string'
              ? booking.dropoffLocation
              : booking.dropoffLocation?.address || 'Drop'
          },
          startTime: new Date(),
          startOdometer: parseFloat(kmReading),
          estimatedDistance: booking.estimatedKm || 0,
          totalCost: booking.totalCost || 0
        };

        const createResponse = await journeyAPI.createJourney(journeyData);
        if (createResponse.data.success) {
          const newJourney = createResponse.data.journey;

          // Now start the journey with tracking
          response = await journeyAPI.startJourney(newJourney._id, {
            startOdometer: parseFloat(kmReading),
            latitude: location?.latitude,
            longitude: location?.longitude
          });

          setJourney(createResponse.data.journey);
        }
      }

      if (response?.data?.success) {
        setTripStarted(true);
        tripStartedRef.current = true;
        setGpsEnabled(true);
        gpsEnabledRef.current = true;
        localStorage.setItem('gpsTrackingEnabled', 'true');
        startGpsWatch();
        // Refresh journey data
        if (response.data.journey) {
          setJourney(prev => ({ ...prev, ...response.data.journey }));
          journeyRef.current = { ...journeyRef.current, ...response.data.journey };
        }
        // Send immediate location update so tracking shows right away
        if (location) {
          sendLocationUpdate(location.latitude, location.longitude);
        }
        // Upload pending odometer photo if captured before trip started
        const jId = response.data.journey?._id || journey?._id || journeyRef.current?._id;
        if (jId && odometerPhoto && !odometerPhotoUploaded) {
          uploadPendingOdometerPhoto(jId);
        }
        // Reset photo state for end-trip photo
        setOdometerPhoto(null);
        setOdometerPhotoUploaded(false);
      }
    } catch (err) {
      console.error('Error starting trip:', err);
      setError(err.response?.data?.message || 'Failed to start trip');
    } finally {
      setUpdating(false);
    }
  };

  // End Trip
  // Show end trip confirmation modal
  const handleEndTripClick = () => {
    if (!kmReading) {
      setError('Please enter the ending KM reading');
      return;
    }
    setShowEndTripModal(true);
  };

  // Actually end the trip after confirmation
  const handleEndTrip = async () => {
    setShowEndTripModal(false);
    setUpdating(true);
    setError(null);

    try {
      const response = await journeyAPI.endJourney(journey._id, {
        endOdometer: parseFloat(kmReading),
        latitude: currentLocation?.latitude,
        longitude: currentLocation?.longitude,
        tollCharges: parseFloat(tollCharges) || 0,
        parkingCharges: parseFloat(parkingCharges) || 0,
        otherCharges: parseFloat(otherCharges) || 0,
        otherChargesNote: otherChargesNote,
        discount: parseFloat(discount) || 0,
        discountNote: discountNote
      });

      if (response.data.success) {
        setTripStarted(false);
        tripStartedRef.current = false;
        setGpsEnabled(false);
        gpsEnabledRef.current = false;
        localStorage.setItem('gpsTrackingEnabled', 'false');
        stopGpsWatch();
        // Navigate to journey details to see invoice
        navigate(`/journeys/${journey._id}`);
      }
    } catch (err) {
      console.error('Error ending trip:', err);
      setError(err.response?.data?.message || 'Failed to end trip');
    } finally {
      setUpdating(false);
    }
  };

  // Manual location update
  const handleManualLocationUpdate = async () => {
    if (!currentLocation || !journey) return;

    setUpdating(true);
    try {
      await sendLocationUpdate(currentLocation.latitude, currentLocation.longitude);
    } finally {
      setUpdating(false);
    }
  };

  // Update KM reading
  const handleUpdateKm = async () => {
    if (!kmReading || !journey) return;

    setUpdating(true);
    try {
      await journeyAPI.updateKmReading(journey._id, {
        kmReading: parseFloat(kmReading)
      });
    } catch (err) {
      console.error('Error updating km:', err);
      setError('Failed to update km reading');
    } finally {
      setUpdating(false);
    }
  };

  // Handle odometer photo capture
  const handleOdometerPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const previewUrl = URL.createObjectURL(file);
    setOdometerPhoto(previewUrl);
    setOdometerPhotoUploaded(false);

    // Get journey ID
    const jId = journey?._id || journeyRef.current?._id;
    if (!jId) {
      // No journey yet (before start trip), store file for later upload
      return;
    }

    // Upload immediately
    setOdometerPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('type', tripStarted ? 'end' : 'start');
      await journeyAPI.uploadOdometerPhoto(jId, formData);
      setOdometerPhotoUploaded(true);
    } catch (err) {
      console.error('Error uploading odometer photo:', err);
      setError('Failed to upload odometer photo');
    } finally {
      setOdometerPhotoUploading(false);
    }
  };

  // Upload pending odometer photo (called after journey is created)
  const uploadPendingOdometerPhoto = async (journeyId) => {
    const fileInput = odometerFileRef.current;
    if (!fileInput?.files?.[0]) return;

    try {
      const formData = new FormData();
      formData.append('photo', fileInput.files[0]);
      formData.append('type', 'start');
      await journeyAPI.uploadOdometerPhoto(journeyId, formData);
      setOdometerPhotoUploaded(true);
    } catch (err) {
      console.error('Error uploading pending odometer photo:', err);
    }
  };

  // Add checkpoint
  const handleAddCheckpoint = async () => {
    if (!journey) return;

    setUpdating(true);
    try {
      await journeyAPI.addCheckpoint(journey._id, {
        location: checkpointNote || 'Checkpoint',
        latitude: currentLocation?.latitude,
        longitude: currentLocation?.longitude,
        purpose: checkpointPurpose,
        notes: checkpointNote,
        kmReading: kmReading ? parseFloat(kmReading) : null
      });

      setShowCheckpointModal(false);
      setCheckpointNote('');
      setCheckpointPurpose('Stop');
    } catch (err) {
      console.error('Error adding checkpoint:', err);
      setError('Failed to add checkpoint');
    } finally {
      setUpdating(false);
    }
  };

  // Toggle GPS tracking
  const handleToggleGps = async () => {
    const newEnabled = !gpsEnabled;

    if (newEnabled) {
      // Enable GPS
      setGpsEnabled(true);
      gpsEnabledRef.current = true;
      localStorage.setItem('gpsTrackingEnabled', 'true');
      startGpsWatch();
    } else {
      // Disable GPS
      setGpsEnabled(false);
      gpsEnabledRef.current = false;
      localStorage.setItem('gpsTrackingEnabled', 'false');
      stopGpsWatch();
    }

    if (journey) {
      try {
        await journeyAPI.toggleTracking(journey._id, { enabled: newEnabled });
      } catch (err) {
        console.error('Error toggling tracking:', err);
      }
    }
  };

  // Get trip data (from journey or booking)
  const tripData = journey || booking;
  const carData = tripData?.carId;
  const customerData = tripData?.customerId;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-600">Loading trip...</p>
        </div>
      </div>
    );
  }

  if (!tripData) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Trip not found</p>
            <Button
              className="mt-4"
              onClick={() => navigate('/journeys')}
            >
              Back to Journeys
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const accuracyInfo = getAccuracyIndicator(gpsAccuracy);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/journeys')}
            className="flex items-center text-gray-600"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="text-sm">Back</span>
          </button>
          <h1 className="text-lg font-bold">
            {tripStarted ? 'Active Trip' : 'Start Trip'}
          </h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2">
          <div className="flex items-center text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        </div>
      )}

      {/* Map Section */}
      <div className="relative">
        <LiveTrackingMap
          driverLocation={currentLocation}
          pickupLocation={
            // Try journey fromLocation coordinates first
            tripData.fromLocation?.coordinates?.latitude
              ? tripData.fromLocation.coordinates
              // Then try first location history entry (captured at trip start)
              : journey?.liveTracking?.locationHistory?.[0]?.latitude
              ? journey.liveTracking.locationHistory[0]
              // Then try local locationHistory
              : locationHistory?.[0]?.latitude
              ? locationHistory[0]
              : null
          }
          dropLocation={
            tripData.toLocation?.coordinates?.latitude
              ? tripData.toLocation.coordinates
              : null
          }
          locationHistory={locationHistory}
          isTracking={tripStarted && gpsEnabled}
          height="250px"
        />

        {/* GPS Status Overlay */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {gpsEnabled && gpsAccuracy && (
            <div className={`bg-white rounded-lg shadow px-2 py-1 text-xs flex items-center`}>
              <span className={`w-2 h-2 rounded-full mr-1.5 bg-${accuracyInfo.color}-500`} />
              GPS: {accuracyInfo.text}
            </div>
          )}
          {locationError && (
            <div className="bg-red-100 text-red-700 rounded-lg shadow px-2 py-1 text-xs">
              {locationError}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Trip Info Card */}
        <Card>
          <CardContent className="p-4">
            {/* Car Info */}
            <div className="flex items-center mb-4">
              {carData?.images?.[0] ? (
                <img
                  src={carData.images[0]}
                  alt="Car"
                  className="w-16 h-16 object-cover rounded-lg mr-3"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                  <Car className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <h2 className="font-bold text-gray-900">
                  {formatVehicleName(carData, { showYear: false })}
                </h2>
                <p className="text-sm text-gray-500">{carData?.registrationNumber}</p>
                {tripStarted && journey?.trackingCode && (
                  <Badge variant="success" className="mt-1">
                    Tracking: {journey.trackingCode}
                  </Badge>
                )}
              </div>
            </div>

            {/* Route */}
            <div className="space-y-3 mb-4">
              <div className="flex items-start">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <MapPin className="h-3.5 w-3.5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Pickup</p>
                  <p className="text-sm font-medium">
                    {typeof tripData.fromLocation === 'string'
                      ? tripData.fromLocation
                      : tripData.fromLocation?.address || tripData.pickupLocation || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <Flag className="h-3.5 w-3.5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Drop</p>
                  <p className="text-sm font-medium">
                    {typeof tripData.toLocation === 'string'
                      ? tripData.toLocation
                      : tripData.toLocation?.address || tripData.dropoffLocation || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            {customerData && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-gray-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium">{customerData.name || 'Customer'}</p>
                    <p className="text-xs text-gray-500">{customerData.phone || tripData.customerPhone}</p>
                  </div>
                </div>
                {(customerData.phone || tripData.customerPhone) && (
                  <a
                    href={`tel:${customerData.phone || tripData.customerPhone}`}
                    className="p-2 bg-green-500 text-white rounded-full"
                  >
                    <Phone className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}

            {/* Trip Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600">Distance</p>
                <p className="text-lg font-bold text-blue-700">
                  {journey?.actualDistance || tripData.estimatedKm || tripData.estimatedDistance || 0} km
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600">Amount</p>
                <p className="text-lg font-bold text-green-700">
                  {formatCurrency(tripData.totalCost || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Control Panel */}
        <Card>
          <CardHeader className="pb-2">
            <h3 className="font-semibold text-gray-900">Trip Controls</h3>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {/* Odometer Section */}
            {tripStarted && journey?.startOdometer && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-600 font-medium">Start Odometer</p>
                    <p className="text-lg font-bold text-green-700">{journey.startOdometer} km</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-green-600 font-medium">Trip Started</p>
                    <p className="text-sm text-green-700">{formatDateTime(journey.startTime)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* KM Reading Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Gauge className="inline h-4 w-4 mr-1" />
                {tripStarted ? 'Current / End Odometer (KM)' : 'Start Odometer (KM)'}
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={kmReading}
                  onChange={(e) => setKmReading(e.target.value)}
                  placeholder={tripStarted ? "Enter current KM reading" : "Enter starting KM"}
                  className="flex-1"
                />
                {/* Camera button for odometer photo */}
                <button
                  type="button"
                  onClick={() => odometerFileRef.current?.click()}
                  className={`px-3 rounded-lg border transition-colors flex items-center gap-1 ${
                    odometerPhotoUploaded
                      ? 'bg-green-50 border-green-300 text-green-600'
                      : odometerPhotoUploading
                      ? 'bg-yellow-50 border-yellow-300 text-yellow-600'
                      : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                  }`}
                  title="Take odometer photo"
                >
                  {odometerPhotoUploaded ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : odometerPhotoUploading ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                </button>
                <input
                  ref={odometerFileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleOdometerPhoto}
                />
                {tripStarted && (
                  <Button
                    variant="outline"
                    onClick={handleUpdateKm}
                    disabled={updating}
                  >
                    Update
                  </Button>
                )}
              </div>
              {/* Odometer photo preview */}
              {odometerPhoto && (
                <div className="mt-2 flex items-center gap-2">
                  <img
                    src={odometerPhoto}
                    alt="Odometer"
                    className="h-16 w-24 object-cover rounded border"
                  />
                  <span className="text-xs text-gray-500">
                    {odometerPhotoUploaded ? 'Uploaded' : odometerPhotoUploading ? 'Uploading...' : 'Ready to upload'}
                  </span>
                </div>
              )}
              {tripStarted && kmReading && journey?.startOdometer && (
                <p className="text-xs text-blue-600 mt-1">
                  Distance covered: <span className="font-semibold">{parseFloat(kmReading) - journey.startOdometer} km</span>
                </p>
              )}
            </div>

            {/* GPS Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
              <div className="flex items-center">
                <Crosshair className="h-5 w-5 text-gray-500 mr-2" />
                <div>
                  <p className="text-sm font-medium">Auto GPS Tracking</p>
                  <p className="text-xs text-gray-500">
                    {gpsEnabled ? 'Location updates enabled' : 'Enable for live tracking'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleGps}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  gpsEnabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    gpsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Current Location Display */}
            {currentLocation && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600">Current Location</p>
                    <p className="text-sm font-mono">
                      {formatCoordinates(currentLocation.latitude, currentLocation.longitude)}
                    </p>
                  </div>
                  {tripStarted && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleManualLocationUpdate}
                      disabled={updating}
                    >
                      <RefreshCw className={`h-4 w-4 ${updating ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              {!tripStarted ? (
                <Button
                  className="w-full"
                  onClick={handleStartTrip}
                  disabled={updating || !kmReading}
                >
                  <Play className="h-5 w-5 mr-2" />
                  {updating ? 'Starting...' : 'Start Trip'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowCheckpointModal(true)}
                    disabled={updating}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Checkpoint
                  </Button>
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={handleEndTripClick}
                    disabled={updating}
                  >
                    <Square className="h-5 w-5 mr-2" />
                    {updating ? 'Ending...' : 'End Trip'}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Waypoints/Checkpoints */}
        {journey?.waypoints?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <h3 className="font-semibold text-gray-900">Checkpoints</h3>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                {journey.waypoints.map((waypoint, index) => (
                  <div key={index} className="flex items-center p-2 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-blue-600">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{waypoint.location || waypoint.purpose}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(waypoint.arrivalTime)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Checkpoint Modal */}
      {showCheckpointModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-2xl p-4">
            <h3 className="text-lg font-bold mb-4">Add Checkpoint</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purpose
              </label>
              <select
                value={checkpointPurpose}
                onChange={(e) => setCheckpointPurpose(e.target.value)}
                className="w-full p-2 border rounded-lg"
              >
                <option value="Stop">Stop</option>
                <option value="Fuel">Fuel Stop</option>
                <option value="Rest">Rest Break</option>
                <option value="Pickup">Pickup</option>
                <option value="Drop">Drop</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <Input
                value={checkpointNote}
                onChange={(e) => setCheckpointNote(e.target.value)}
                placeholder="Add a note..."
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCheckpointModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleAddCheckpoint}
                disabled={updating}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* End Trip Confirmation Modal */}
      <Modal
        isOpen={showEndTripModal}
        onClose={() => setShowEndTripModal(false)}
        title="End Trip & Generate Invoice"
        size="lg"
      >
        <div className="max-h-[70vh] overflow-y-auto">
          {/* Trip Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-blue-800 mb-3 text-sm flex items-center">
              <Route className="h-4 w-4 mr-2" />
              Trip Summary
            </h4>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div className="text-center p-2 bg-white rounded">
                <p className="text-gray-500 text-xs">Start KM</p>
                <p className="font-bold text-lg">{journey?.startOdometer || 0}</p>
              </div>
              <div className="text-center p-2 bg-white rounded">
                <p className="text-gray-500 text-xs">End KM</p>
                <p className="font-bold text-lg">{kmReading || 0}</p>
              </div>
              <div className="text-center p-2 bg-white rounded">
                <p className="text-gray-500 text-xs">Distance</p>
                <p className="font-bold text-lg text-blue-600">
                  {kmReading && journey?.startOdometer
                    ? `${parseFloat(kmReading) - journey.startOdometer}`
                    : '0'} km
                </p>
              </div>
              <div className="text-center p-2 bg-white rounded">
                <p className="text-gray-500 text-xs">Rate/KM</p>
                <p className="font-bold text-lg text-green-600">
                  {formatCurrency(journey?.carId?.pricePerKm || booking?.carId?.pricePerKm || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Charges */}
          <div className="bg-yellow-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-yellow-800 mb-3 text-sm">Additional Charges</h4>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Toll Charges</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={tollCharges}
                  onChange={(e) => setTollCharges(e.target.value)}
                  className="text-sm h-9"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Parking Charges</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={parkingCharges}
                  onChange={(e) => setParkingCharges(e.target.value)}
                  className="text-sm h-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Other Charges</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={otherCharges}
                  onChange={(e) => setOtherCharges(e.target.value)}
                  className="text-sm h-9"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Other Charges Note</label>
                <Input
                  type="text"
                  placeholder="e.g., Night charges, Extra stop"
                  value={otherChargesNote}
                  onChange={(e) => setOtherChargesNote(e.target.value)}
                  className="text-sm h-9"
                />
              </div>
            </div>
          </div>

          {/* Discount */}
          <div className="bg-green-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-green-800 mb-3 text-sm">Discount (Optional)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Discount Amount</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="text-sm h-9"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Discount Reason</label>
                <Input
                  type="text"
                  placeholder="e.g., Regular customer, Promo"
                  value={discountNote}
                  onChange={(e) => setDiscountNote(e.target.value)}
                  className="text-sm h-9"
                />
              </div>
            </div>
          </div>

          {/* Invoice Preview / Cost Breakdown */}
          <div className="bg-gray-900 text-white rounded-lg p-4 mb-4">
            <h4 className="font-semibold mb-3 text-sm border-b border-gray-700 pb-2">Invoice Preview</h4>
            <div className="space-y-2 text-sm">
              {/* Base Fare */}
              <div className="flex justify-between">
                <span className="text-gray-300">
                  Base Fare ({kmReading && journey?.startOdometer ? parseFloat(kmReading) - journey.startOdometer : 0} km × {formatCurrency(journey?.carId?.pricePerKm || booking?.carId?.pricePerKm || 0)})
                </span>
                <span className="font-medium">
                  {formatCurrency(
                    (kmReading && journey?.startOdometer && (journey?.carId?.pricePerKm || booking?.carId?.pricePerKm))
                      ? (parseFloat(kmReading) - journey.startOdometer) * (journey?.carId?.pricePerKm || booking?.carId?.pricePerKm || 0)
                      : (journey?.baseCost || booking?.totalCost || 0)
                  )}
                </span>
              </div>

              {/* Toll */}
              {parseFloat(tollCharges) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Toll Charges</span>
                  <span className="font-medium">+ {formatCurrency(parseFloat(tollCharges))}</span>
                </div>
              )}

              {/* Parking */}
              {parseFloat(parkingCharges) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Parking Charges</span>
                  <span className="font-medium">+ {formatCurrency(parseFloat(parkingCharges))}</span>
                </div>
              )}

              {/* Other Charges */}
              {parseFloat(otherCharges) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Other Charges {otherChargesNote && `(${otherChargesNote})`}</span>
                  <span className="font-medium">+ {formatCurrency(parseFloat(otherCharges))}</span>
                </div>
              )}

              {/* Subtotal */}
              <div className="flex justify-between pt-2 border-t border-gray-700">
                <span className="text-gray-300">Subtotal</span>
                <span className="font-medium">
                  {formatCurrency(
                    ((kmReading && journey?.startOdometer && (journey?.carId?.pricePerKm || booking?.carId?.pricePerKm))
                      ? (parseFloat(kmReading) - journey.startOdometer) * (journey?.carId?.pricePerKm || booking?.carId?.pricePerKm || 0)
                      : (journey?.baseCost || booking?.totalCost || 0))
                    + (parseFloat(tollCharges) || 0)
                    + (parseFloat(parkingCharges) || 0)
                    + (parseFloat(otherCharges) || 0)
                  )}
                </span>
              </div>

              {/* Discount */}
              {parseFloat(discount) > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Discount {discountNote && `(${discountNote})`}</span>
                  <span className="font-medium">- {formatCurrency(parseFloat(discount))}</span>
                </div>
              )}

              {/* Grand Total */}
              <div className="flex justify-between pt-3 border-t-2 border-gray-600 mt-2">
                <span className="text-lg font-bold">TOTAL AMOUNT</span>
                <span className="text-2xl font-bold text-green-400">
                  {formatCurrency(
                    ((kmReading && journey?.startOdometer && (journey?.carId?.pricePerKm || booking?.carId?.pricePerKm))
                      ? (parseFloat(kmReading) - journey.startOdometer) * (journey?.carId?.pricePerKm || booking?.carId?.pricePerKm || 0)
                      : (journey?.baseCost || booking?.totalCost || 0))
                    + (parseFloat(tollCharges) || 0)
                    + (parseFloat(parkingCharges) || 0)
                    + (parseFloat(otherCharges) || 0)
                    - (parseFloat(discount) || 0)
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 sticky bottom-0 bg-white pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowEndTripModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={handleEndTrip}
              disabled={updating}
            >
              {updating ? 'Processing...' : 'Complete Trip & Generate Invoice'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ActiveTrip;
